import { useTranslation } from 'react-i18next'
import { User } from '../types/models'
import { DataTable, DataTableColumns } from './atoms/DataTable'
import { DiagnosticsIndicator } from './DiagnosticsIndicator'
import { FcrStatusIndicator } from './FcrStatusIndicator'
import { Box } from '@mui/system'
import { TablePagination, TextField } from '@mui/material'
import { LinkTo } from './atoms/LinkTo'
import { ChangeEvent, useMemo, useState } from 'react'

export const USERS_PER_PAGE_OPTIONS = [20, 100, 500]

export interface UserDataTableProps {
  users: User[]
  searchbarID?: string
}

export function UserDataTable(props: UserDataTableProps): React.ReactElement {

  const { users, searchbarID } = props
  const { t } = useTranslation()
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(USERS_PER_PAGE_OPTIONS[0])

  const filteredUsers = useMemo(
    () => users.filter((user) => user.email.toLowerCase().includes(search) || `${user.id}` === search),
    [users, search]
  )

  // The list may shrink under the current page (search, user removed), so clamp.
  const lastPage = Math.max(0, Math.ceil(filteredUsers.length / rowsPerPage) - 1)
  const currentPage = Math.min(page, lastPage)
  const pageUsers = filteredUsers.slice(currentPage * rowsPerPage, (currentPage + 1) * rowsPerPage)

  const columns: DataTableColumns<User> = {
    [t('Column-ID')]: 'id',
    [t('Column-Diagnostics')]: (user: User) => (
      <DiagnosticsIndicator diagnostics={user.diagnostics} userId={user.id!} userEmail={user.email} />
    ),
    [t('Column-Email')]: (user: User) => (
      <LinkTo url={`/admin/users/${user.id}`} title={user.email} />
    ),
    [t('Column-Groups')]: (user: User) => user.groups?.join(', ') || '',
    [t('Column-FCR')]: (user: User) => (
      <FcrStatusIndicator devices={user.fcr_devices || []} userEmail={user.email} />
    ),
  }

  const handleSearchEmail = (event: ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value.trim().toLowerCase())
    setPage(0)
  }

  const handleRowsPerPage = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <TextField
            sx={{ '& ::placeholder': { fontSize: '14px' }, mb: 2 }}
              margin="normal"
              name="users-search-bar"
              placeholder={t('Search by email')}
              type="search"
              id={searchbarID}
              size="small"
              onChange={handleSearchEmail}
          />
      </Box>
      <DataTable dataCy='box-Users admin table' data={pageUsers} columns={columns} />
      <TablePagination
        data-cy='users-pagination'
        component='div'
        count={filteredUsers.length}
        page={currentPage}
        onPageChange={(_event, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        rowsPerPageOptions={USERS_PER_PAGE_OPTIONS}
        onRowsPerPageChange={handleRowsPerPage}
        labelRowsPerPage={t('Users per page')}
        labelDisplayedRows={({ from, to, count }) => t('Pagination-displayed-rows', { from, to, count })}
        getItemAriaLabel={(type) => t(`Pagination-${type}`)}
      />
    </>
  )
}
